import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SCENARIOS, STAGES, buildBeats, locate, totalOf } from "./story";

const ORDER = ["runner", "fast", "rejected", "loss"];

export function useTimeline() {
  const [scenarioId, setScenarioId] = useState("runner");
  const sc = SCENARIOS[scenarioId];
  const beats = useMemo(() => buildBeats(sc), [sc]);
  const total = totalOf(beats);
  const clock = useRef({ t: 0, playing: true, speed: 1 });
  const [tick, setTick] = useState({ t: 0, playing: true, speed: 1 });
  const live = useRef({ beats, total, scenarioId });
  live.current = { beats, total, scenarioId };

  useEffect(() => {
    let raf, last = performance.now(), lastUi = 0;
    const loop = (now) => {
      const c = clock.current, dt = Math.min(0.25, (now - last) / 1000);
      last = now;
      if (c.playing) c.t += dt * c.speed;
      if (c.t >= live.current.total) {
        c.t = 0;
        setScenarioId(ORDER[(ORDER.indexOf(live.current.scenarioId) + 1) % ORDER.length]);
      }
      if (now - lastUi > 90) { lastUi = now; setTick({ t: c.t, playing: c.playing, speed: c.speed }); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const sync = () => setTick({ ...clock.current });
  const play = useCallback(() => { clock.current.playing = true; sync(); }, []);
  const pause = useCallback(() => { clock.current.playing = false; sync(); }, []);
  const toggle = useCallback(() => { clock.current.playing = !clock.current.playing; sync(); }, []);
  const restart = useCallback(() => { clock.current.t = 0; clock.current.playing = true; sync(); }, []);
  const setSpeed = useCallback((s) => { clock.current.speed = s; sync(); }, []);
  const seekStage = useCallback((stageId) => {
    const b = live.current.beats.find((x) => x.stage === stageId);
    if (b) { clock.current.t = b.start + 0.01; sync(); }
  }, []);
  const chooseScenario = useCallback((id) => { clock.current.t = 0; clock.current.playing = true; setScenarioId(id); sync(); }, []);

  const pos = locate(beats, Math.min(tick.t, total - 0.001));
  const stageIdx = STAGES.findIndex((s) => s.id === pos.beat.stage);
  const stagesInPlay = new Set(beats.map((b) => b.stage));
  return { clock, beats, total, sc, scenarioId, ...tick, ...pos, stageIdx, stagesInPlay, play, pause, toggle, restart, setSpeed, seekStage, chooseScenario };
}

export function usePrefersReducedMotion() {
  const [r, setR] = useState(() => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false);
  useEffect(() => {
    const m = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!m) return undefined;
    const f = (e) => setR(e.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return [r, setR];
}

export function useIsMobile() {
  const [m, setM] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const f = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return m;
}

export function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl2") || c.getContext("webgl")));
  } catch {
    return false;
  }
}
